import { ChangeDetectorRef, Component, Inject, OnDestroy, Optional, ViewChild } from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogRef
} from '@angular/material/dialog';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { U2bValidators } from '@shared/validators/validators';
import { ProductsApiService } from '@modules/bcm/@shared/services';
import { AppNotificationService } from '@core/services/app-notification.service';
import { PayableOption } from '@shared/models/payable-option';
import { PayableOptionId, ProductPayableOptionApiService } from '@bcmApiServices/product-payable-option.api-service';
import { hours, minutes } from '@shared/constants/date';
import { Season } from '@shared/models/seasons';
import { SeasonApiService } from '@bcmApiServices/seasons.api-service';
import {
    addDays,
    addHours,
    addMonths,
    addWeeks,
    addYears,
    endOfMonth,
    isFirstDayOfMonth,
    startOfToday,
    startOfYear,
    subMinutes,
} from '@core/date.facade';
import { isValidDate } from '@core/functions/is-valid-date';
import { U2bDateValidators } from '@shared/validators/date/date-validators';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BcmService } from '@modules/bcm/bcm.service';
import { ProductSubscription } from '@shared/models/product-subscription';
import { TolgeeService } from '@core/translation/tolgee.service';

@Component({
    selector: 'get-product-subscription-dates-dialog',
    templateUrl: './get-product-subscription-dates-dialog.component.html',
    styleUrls: ['./get-product-subscription-dates-dialog.component.scss'],
    standalone: false
})
export class GetProductSubscriptionDatesDialogComponent implements OnDestroy {
    private _unsubAll = new Subject();
    formGroup: UntypedFormGroup;
    payableOptions: PayableOption[];
    PayableOptionId = PayableOptionId;
    seasons: Season[];
    seasonFormControl = new FormControl<Season>(null);
    hours = hours;
    minutes = minutes;
    isSaving: boolean;
    categories: string[];

    @ViewChild(MatAutocompleteTrigger, {read: MatAutocompleteTrigger})
    matAutocompleteTrigger: MatAutocompleteTrigger;

    constructor(
        public dialogRef: MatDialogRef<GetProductSubscriptionDatesDialogComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: {
            initialFromDate: Date,
            initialPayableOption: PayableOption,
            subscription?: ProductSubscription
        },
        private _formBuilder: UntypedFormBuilder,
        private _productApiService: ProductsApiService,
        private _productPayableOptionApiService: ProductPayableOptionApiService,
        private _seasonApiService: SeasonApiService,
        private _appNotificationService: AppNotificationService,
        private _tolgee: TolgeeService,
        private _bcmService: BcmService,
        private _changeDetectorRef: ChangeDetectorRef,
    ) {
        this.dialogRef.addPanelClass('default-dialog');
        this.formGroup = this._createForm(
            data.initialFromDate,
            data.initialPayableOption,
            data.subscription
        );

        this._loadPayableOptions();
        this._loadSeasons();

        // The workflow opens this dialog without a payable option, so the season can only follow once "jährlich" is picked.
        this.formGroup.get('payableOption').valueChanges
            .pipe(takeUntil(this._unsubAll))
            .subscribe(() => this._syncVestingSeasonFromDynamicPrice(this.seasons));

        this.seasonFormControl.valueChanges
            .pipe(takeUntil(this._unsubAll))
            .subscribe(season => {
                this.formGroup.patchValue({
                    vestingPeriodFrom: season?.startDate || null,
                    vestingPeriodUntil: season?.endDate || null
                });
            });

    }

    ngOnDestroy(): void {
        this._unsubAll.next(undefined);
        this._unsubAll.complete();
    }

    saveAndContinue(): void {
        this.formGroup.updateValueAndValidity({emitEvent: false});

        let invalid = false;

        for (const key in this.formGroup.controls) {
            if (key !== 'dynamicPriceFormGroup') {
                if (this.formGroup.controls[key].invalid) {
                    this.formGroup.controls[key].markAsTouched();
                    invalid = true;
                }
            }
        }

        if (invalid) {
            this._appNotificationService.showError(this._tolgee.translate('common.checkFieldsHighlight'));
            return;
        }

        const productSubscription = this.formGroup.getRawValue();

        this.isSaving = true;
        this.dialogRef.close(productSubscription);
    }

    comparePayableOptions(payableOption1: PayableOption, payableOption2: PayableOption): boolean {
        return payableOption1?.id === payableOption2?.id;
    }

    compareSeasons(season1: Season, season2: Season): boolean {
        return season1?.id === season2?.id;
    }

    private _createForm(initialFromDate: Date, initialPayableOption: PayableOption, subscription: ProductSubscription): UntypedFormGroup {
        const form = this._formBuilder.group({
            payableOption: [
                subscription?.payableOption || initialPayableOption || null,
                [U2bValidators.required(this._tolgee.translate('common.validationMsg.selectPaymentInterval'))],
            ],
            fromDate: [
                subscription?.fromDate ||
                initialFromDate || null,
                [U2bValidators.required(this._tolgee.translate('common.validationMsg.subscriptionStartDateRequired'))]
            ],
            toDate: [
                subscription?.toDate || null],
            vestingPeriodFrom: [
                subscription?.vestingPeriodFrom ||
                null,
                [U2bValidators.required(this._tolgee.translate('common.validationMsg.servicePeriodRequired'))]
            ],
            vestingPeriodUntil: [
                subscription?.vestingPeriodUntil ||
                {value: null, disabled: false},
                [U2bValidators.required(this._tolgee.translate('common.validationMsg.servicePeriodRequired'))]
            ],
            lastDayOfMonth: [
                subscription?.lastDayOfMonth || false],
        }, {
            validators: [
                U2bDateValidators.dateBeforeOtherDate(
                    'fromDate',
                    'toDate',
                    this._tolgee.translate('dialogs.productSubscription.labelFirstPosition'),
                    this._tolgee.translate('dialogs.productSubscription.labelLastPosition'),
                ),
                U2bDateValidators.dateBeforeOtherDate(
                    'vestingPeriodFrom',
                    'vestingPeriodUntil',
                    this._tolgee.translate('dialogs.productSubscription.vestingPeriodStart'),
                    this._tolgee.translate('dialogs.productSubscription.vestingPeriodEnd'),
                ),
            ],
        });

        const payableOptionField = form.get('payableOption');
        const vestingPeriodFromField = form.get('vestingPeriodFrom');
        const vestingPeriodUntilField = form.get('vestingPeriodUntil');
        const lastDayOfMonthField = form.get('lastDayOfMonth');

        vestingPeriodFromField
            .valueChanges
            .pipe(takeUntil(this._unsubAll))
            .subscribe(value => {
                if (isValidDate(value) && payableOptionField.value && vestingPeriodUntilField.value == null) {
                    let newDate: Date;
                    let setUntilDateToLastDayOfMonth = false;
                    const firstDayOfMonth = isFirstDayOfMonth(value);

                    switch (payableOptionField.value.id) {
                        case PayableOptionId.Yearly:
                            newDate = addYears(value, 1);
                            break;
                        case PayableOptionId.HalfYearly:
                            newDate = addMonths(value, 6);
                            setUntilDateToLastDayOfMonth = true;
                            break;
                        case PayableOptionId.QuarterYearly:
                            newDate = addMonths(value, 3);
                            setUntilDateToLastDayOfMonth = true;
                            break;
                        case PayableOptionId.Monthly:
                            newDate = addMonths(value, 1);
                            setUntilDateToLastDayOfMonth = true;
                            break;
                        case PayableOptionId.Weekly:
                            newDate = addWeeks(value, 1);
                            break;
                        case PayableOptionId.Daily:
                            newDate = addDays(value, 1);
                            break;
                        case PayableOptionId.Hourly:
                            newDate = addHours(value, 1);
                            break;
                    }

                    vestingPeriodUntilField.patchValue(subMinutes(newDate, 1));

                    if (firstDayOfMonth && setUntilDateToLastDayOfMonth) {
                        lastDayOfMonthField.setValue(true);
                    }
                }
            });

        lastDayOfMonthField
            .valueChanges
            .pipe(takeUntil(this._unsubAll))
            .subscribe(value => {
                if (value) {
                    form.get('vestingPeriodUntil').disable();
                    form.get('vestingPeriodUntil').patchValue(endOfMonth(form.get('vestingPeriodUntil').value || form.get('vestingPeriodFrom').value));
                } else {
                    form.get('vestingPeriodUntil').enable();
                }
            });

        if (subscription && subscription.lastDayOfMonth) {
            form.get('vestingPeriodUntil').disable();
        }

        return form;
    }

    private _loadPayableOptions(): void {
        this._productPayableOptionApiService
            .getAll()
            .pipe(takeUntil(this._unsubAll))
            .subscribe((payableOptions: PayableOption[]) => {
                this.payableOptions = payableOptions.filter((po => po.id < 100));
            });
    }

    private _loadSeasons(): void {
        this._seasonApiService
            .getAllSeasons()
            .pipe(takeUntil(this._unsubAll))
            .subscribe((seasons: Season[]) => {
                this.seasons = seasons;
                this._syncVestingSeasonFromDynamicPrice(seasons);
            });
    }

    private _syncVestingSeasonFromDynamicPrice(seasons: Season[]): void {
        const seasonId = this.data.subscription?.dynamicPrice?.season?.id;
        const payableOption = this.formGroup?.get('payableOption')?.value as PayableOption | null;

        if (!seasonId || payableOption?.id !== PayableOptionId.Yearly) {
            return;
        }

        const match = seasons?.find((season) => season.id === seasonId);

        if (!match || this.seasonFormControl.value?.id === match.id) {
            return;
        }

        this.seasonFormControl.setValue(match);
    }
}
